import type { Parameter } from "@globals";
import { ContextualError } from "@lib/utils/ContextualError";

export type ParameterWithMapping = {
  parameter: Parameter;
  idMapping: Map<string, string>;
};

/* This is needed because category IDs are linked to property values. */
export const generateNewParameterIDs = (parameter: Parameter): ParameterWithMapping => {
  const newParameterId = crypto.randomUUID();

  if (parameter.type !== "categorical") {
    const idMapping = new Map([[parameter.id, newParameterId]]);

    return {
      parameter: {
        ...parameter,
        id: newParameterId
      },
      idMapping
    };
  }

  // For categorical parameters, we need to generate new IDs for categories and properties, and link them correctly.
  // Create mapping of old IDs to new UUIDs (including the parameter itself)
  const oldToNewIdMap = new Map([
    [parameter.id, newParameterId],
    ...parameter.categories.map(category => [category.id, crypto.randomUUID()] as [string, string])
  ]);

  function getNewCategoryId(oldId: string): string {
    const newId = oldToNewIdMap.get(oldId);

    if (!newId) {
      throw new ContextualError("No new ID found for old category ID", {
        old_id: oldId,
        id_map: Object.fromEntries(oldToNewIdMap)
      });
    }

    return newId;
  }

  return {
    parameter: {
      ...parameter,
      id: newParameterId,
      categories: parameter.categories.map(category => ({
        ...category,
        id: getNewCategoryId(category.id)
      })),
      properties: parameter.properties.map(property => ({
        ...property,
        id: crypto.randomUUID(),
        category_property_values: property.category_property_values.map(value => ({
          ...value,
          category_id: getNewCategoryId(value.category_id)
        }))
      }))
    },
    idMapping: oldToNewIdMap
  };
};
